import { customElement, property, html, css, type TemplateResult } from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { textInputContextMenuItems } from '../text-input-contextmenu.js';
import { demoFunc } from './dees-input-textarea.demo.js';
import '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';

declare global { interface HTMLElementTagNameMap { 'dees-input-textarea': DeesInputTextarea; } }

@customElement('dees-input-textarea')
export class DeesInputTextarea extends DeesInputBase<DeesInputTextarea> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];
  public static inputLayout = 'compound' as const;

  @property({ type: String }) accessor value = '';
  @property({ type: String }) accessor placeholder = '';
  @property({ type: Number }) accessor rows = 3;
  @property({ type: Number }) accessor maxLength = -1;
  @property({ type: String, reflect: true }) accessor validationState: 'valid' | 'warn' | 'invalid' | undefined;
  @property({ type: String }) accessor validationText = '';
  @property({ attribute: false }) accessor validationFunction: ((value: string) => { valid: boolean; message?: string }) | undefined;
  private touched = false;
  private lastValidity: boolean | undefined;

  public static styles = [...DeesInputBase.baseStyles, css`
    textarea { display: block; width: 100%; height: auto; padding: 10px 12px; resize: vertical; text-align: left; }
    .field-messages { grid-column: 1 / -1; }
    .validationText { font-size: var(--dees-font-footnote-size); line-height: var(--dees-font-footnote-line); color: var(--dees-color-text-error); }
    .validationText:not(:empty) { margin-top: var(--dees-spacing-xs); }
    :host([validationstate="invalid"]) textarea { border-color: var(--dees-color-accent-error); }
    :host([validationstate="warn"]) .validationText { color: var(--dees-color-text-warning); }
    :host([validationstate="valid"]) .validationText { color: var(--dees-color-text-success); }
  `];

  public render(): TemplateResult {
    return html`<div class="input-wrapper">
      <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required} @click=${() => this.focus()}></dees-label>
      <div class="input-container">
        <textarea class="field-control" .value=${this.value} .rows=${Math.max(1, this.rows)}
          placeholder=${this.placeholder} ?disabled=${this.disabled} ?required=${this.required}
          aria-label=${this.label ?? ''} aria-invalid=${this.validationState === 'invalid' ? 'true' : 'false'}
          aria-describedby="field-description field-validation"
          @input=${this.handleInput} @blur=${() => this.validate()}></textarea>
      </div>
      <div class="field-messages">
        <div id="field-description">${this.renderDescription()}</div>
        <div id="field-validation" class="validationText" role="status">${this.validationText}</div>
      </div>
    </div>`;
  }

  public willUpdate(changed: Map<PropertyKey, unknown>): void {
    super.willUpdate(changed);
    if (changed.has('value') && this.value == null) this.value = '';
  }

  public updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    const input = this.shadowRoot?.querySelector('textarea');
    if (this.maxLength >= 0) input?.setAttribute('maxlength', String(this.maxLength));
    else input?.removeAttribute('maxlength');
    if (['value', 'required', 'maxLength', 'validationFunction', 'disabled'].some(key => changed.has(key))) {
      this.validate(this.touched || Boolean(this.value));
    } else if (changed.has('validationState') || changed.has('validationText')) {
      this.syncValidity();
    }
  }

  private ruleMessage(): string {
    if (this.disabled) return '';
    if (this.required && !this.value.length) return 'Enter a value.';
    if (this.maxLength >= 0 && this.value.length > this.maxLength) return `Use ${this.maxLength} characters or fewer.`;
    const result = this.validationFunction?.(this.value);
    return result && !result.valid ? result.message || 'Check this value.' : '';
  }

  private syncValidity(): void {
    const message = this.disabled ? '' : this.ruleMessage() || (this.validationState === 'invalid' ? this.validationText || 'Check this value.' : '');
    this.shadowRoot?.querySelector('textarea')?.setCustomValidity(message);
    const valid = !message;
    if (valid !== this.lastValidity) {
      this.lastValidity = valid;
      this.dispatchEvent(new CustomEvent('validation-change', { detail: { valid, message }, bubbles: true, composed: true }));
    }
  }

  public validate(showMessage = true): boolean {
    if (showMessage) this.touched = true;
    const message = this.ruleMessage();
    this.validationText = showMessage ? message : '';
    this.validationState = showMessage ? message ? 'invalid' : 'valid' : undefined;
    this.syncValidity();
    return !message;
  }

  public checkValidity(): boolean {
    return this.disabled || (!this.ruleMessage() && this.validationState !== 'invalid');
  }

  public reportValidity(): boolean {
    // Preserve a host-supplied validation message until the value changes.
    const valid = this.validationState === 'invalid' ? this.checkValidity() : this.validate();
    if (!valid) this.focus();
    return valid;
  }

  private handleInput = (): void => {
    if (this.disabled) return;
    this.value = this.shadowRoot!.querySelector('textarea')!.value;
    this.validate();
    this.changeSubject.next(this);
  };

  public getContextMenuItems() {
    return textInputContextMenuItems({ input: this.shadowRoot!.querySelector('textarea')!, disabled: this.disabled,
      commit: value => { this.value = value; this.validate(); this.changeSubject.next(this); } });
  }

  public getValue(): string { return this.value; }
  public setValue(value: string | null): void {
    this.value = value ?? '';
    this.touched = false;
    this.validate(false);
  }
  public reset(): void { this.setValue(''); }
  public focus(options?: FocusOptions): void { this.shadowRoot?.querySelector('textarea')?.focus(options); }
  public blur(): void { this.shadowRoot?.querySelector('textarea')?.blur(); }
}
